<script setup lang="ts">
import { PUBLIC_NAVIGATION_DESTINATIONS } from '~/shared-kernel/presentation/navigation/public-navigation'
import {
  CUSTOMER_ACCOUNT_DESTINATION,
  CUSTOMER_RIGHTS_DESTINATION,
  customerNavigationTarget,
  isCustomerNavigationActive
} from '~/shared-kernel/presentation/navigation/customer-navigation'
import CustomerAccountMenu from '~/shared-kernel/presentation/navigation/CustomerAccountMenu.vue'
import CustomerMobileNavigation from '~/shared-kernel/presentation/navigation/CustomerMobileNavigation.vue'

interface PublicNavigationItem {
  id: 'home' | 'events'
  label: string
  icon: string
  path: string
}

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const menuOpen = ref(false)
const menuButton = ref<HTMLButtonElement | null>(null)
const topbar = ref<HTMLElement | null>(null)
const { $customerAccount: customerAccount } = useNuxtApp()
// The server-side session hint keeps SSR and hydration on the same variant, so nothing swaps after mount.
const authenticated = computed(() => customerAccount.signedIn.value)

// Guests sign in without an explicit redirect so they land on the default destination (their tickets).
const accountDestination = computed(() => ({
  label: t(CUSTOMER_ACCOUNT_DESTINATION.labelKey),
  to: localePath('/account/login')
}))
// Signed-in visitors keep their wallet one click away on every page, public or account.
const rightsTarget = computed(() => customerNavigationTarget(CUSTOMER_RIGHTS_DESTINATION, localePath))

const navigation = computed<PublicNavigationItem[]>(() => {
  return PUBLIC_NAVIGATION_DESTINATIONS.map(destination => ({
    id: destination.id,
    label: t(destination.labelKey),
    icon: destination.icon,
    path: localePath(destination.path)
  }))
})

function isNavigationActive(item: PublicNavigationItem) {
  if (item.id === 'events') {
    return route.path === item.path || route.path.startsWith(`${item.path}/`)
  }
  return route.path === item.path
}

watch(() => route.fullPath, () => {
  menuOpen.value = false
})

function closeOnEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !menuOpen.value || event.defaultPrevented) return
  if (event.target instanceof Element && event.target.closest('[role=dialog]')) return
  menuOpen.value = false
  void nextTick(() => menuButton.value?.focus())
}

async function toggleMenu() {
  menuOpen.value = !menuOpen.value
  if (!menuOpen.value) return
  await nextTick()
  requestAnimationFrame(() => {
    if (menuOpen.value) topbar.value?.querySelector<HTMLElement>('.public-topbar__nav a')?.focus()
  })
}

function closeOnOutsidePointer(event: PointerEvent) {
  if (!menuOpen.value || topbar.value?.contains(event.target as Node)) return
  menuOpen.value = false
  void nextTick(() => menuButton.value?.focus())
}

function closeOnExpandedNavigation() {
  if (window.innerWidth >= 768) menuOpen.value = false
}

async function handleNavigation(event: MouseEvent, item: PublicNavigationItem) {
  menuOpen.value = false
  const modifiedClick = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  if (modifiedClick || item.id !== 'home' || route.path !== item.path) return

  event.preventDefault()
  if (route.hash) await navigateTo(item.path)
  window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
}

function handleBrandNavigation(event: MouseEvent) {
  const home = navigation.value.find(item => item.id === 'home')
  if (home) void handleNavigation(event, home)
}

onMounted(() => {
  // Resolve the customer session without blocking public navigation; guests keep the sign-in access.
  void customerAccount.hydrateInBackground().catch(() => {})
  document.addEventListener('keydown', closeOnEscape)
  document.addEventListener('pointerdown', closeOnOutsidePointer)
  window.addEventListener('resize', closeOnExpandedNavigation, { passive: true })
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', closeOnEscape)
  document.removeEventListener('pointerdown', closeOnOutsidePointer)
  window.removeEventListener('resize', closeOnExpandedNavigation)
})
</script>

<template>
  <header ref="topbar" class="public-topbar tq-navigation-type" :class="{ 'public-topbar--session': authenticated }" :aria-label="t('public.primaryHeaderAria')">
    <NuxtLink
      class="public-topbar__brand"
      :to="localePath('/')"
      :aria-label="$t('public.brand')"
      @click.capture="handleBrandNavigation"
    >
      <TqBrandLogo class="public-topbar__logo-stage" contrast-surface width="300" height="74" />
    </NuxtLink>

    <!-- Signed-in phones open everything from the avatar, so the hamburger would only duplicate it. -->
    <button
      v-if="!authenticated"
      ref="menuButton"
      class="public-topbar__menu-button"
      type="button"
      :aria-expanded="menuOpen"
      aria-controls="public-navigation"
      :aria-label="$t(menuOpen ? 'public.navigation.closeMenu' : 'public.navigation.openMenu')"
      @click="toggleMenu"
    >
      <UIcon
        :key="menuOpen ? 'close' : 'open'"
        class="public-topbar__menu-icon"
        :name="menuOpen ? 'i-lucide-x' : 'i-lucide-menu'"
        aria-hidden="true"
      />
    </button>
    <!-- Icon CSS is injected at runtime the first time an icon renders, which restyles the whole page on
         the first tap. Rendering the close icon hidden ships its CSS with the SSR head instead. -->
    <UIcon v-if="!authenticated" name="i-lucide-x" class="hidden" aria-hidden="true" />

    <div id="public-navigation" class="public-topbar__panel" :class="{ 'public-topbar__panel--open': menuOpen }">
      <nav class="public-topbar__nav" :aria-label="t('public.primaryNavigationAria')">
        <NuxtLink
          v-for="item in navigation"
          :key="item.id"
          :to="item.path"
          :class="{ 'public-topbar__nav-link--active': isNavigationActive(item) }"
          :aria-label="item.label"
          :aria-current="isNavigationActive(item) ? 'page' : undefined"
          :title="item.label"
          @click.capture="handleNavigation($event, item)"
        >
          <UIcon :name="item.icon" aria-hidden="true" />
          <span class="public-topbar__nav-label">{{ item.label }}</span>
        </NuxtLink>
      </nav>

      <div v-if="!authenticated" class="public-topbar__mobile-access">
        <NuxtLink :to="accountDestination.to" @click="menuOpen = false">
          <UIcon :name="CUSTOMER_ACCOUNT_DESTINATION.icon" aria-hidden="true" />
          {{ accountDestination.label }}
        </NuxtLink>
      </div>
      <div class="public-topbar__mobile-controls">
        <LocaleSwitcher />
        <ThemeSwitcher />
      </div>
    </div>

    <div class="public-topbar__controls">
      <TqButton
        v-if="authenticated"
        class="public-topbar__rights"
        :to="rightsTarget"
        :icon="CUSTOMER_RIGHTS_DESTINATION.icon"
        :aria-current="isCustomerNavigationActive(route, CUSTOMER_RIGHTS_DESTINATION, localePath) ? 'page' : undefined"
      >{{ t(CUSTOMER_RIGHTS_DESTINATION.labelKey) }}</TqButton>
      <div v-if="authenticated" class="public-topbar__session" @pointerdown="menuOpen = false">
        <CustomerAccountMenu>
          <template #before="{ close }">
            <div class="public-topbar__account-general">
              <NuxtLink v-for="item in navigation" :key="item.id" :to="item.path" :aria-current="isNavigationActive(item) ? 'page' : undefined" @click="close()">
                <UIcon :name="item.icon" aria-hidden="true" />
                <span>{{ item.label }}</span>
              </NuxtLink>
            </div>
          </template>
          <template v-if="$slots.help" #help>
            <slot name="help" />
          </template>
        </CustomerAccountMenu>
      </div>
      <NuxtLink
        v-else
        class="public-topbar__account"
        :to="accountDestination.to"
        :aria-label="accountDestination.label"
        :title="accountDestination.label"
      >
        <UIcon :name="CUSTOMER_ACCOUNT_DESTINATION.icon" aria-hidden="true" />
        <span class="public-topbar__access-label">{{ accountDestination.label }}</span>
      </NuxtLink>
      <LocaleSwitcher />
      <ThemeSwitcher />
    </div>
  </header>
  <!-- Purchase steps own the bottom of the screen with their own bar. -->
  <CustomerMobileNavigation v-if="authenticated && !route.meta.purchaseFlow" />
</template>

<style scoped>
.public-topbar {
  --public-navigation-text: var(--tq-topbar-text);
  --public-navigation-icon: var(--tq-topbar-text);
  --public-navigation-muted: var(--tq-topbar-text);
  --public-topbar-line: var(--tq-nav-active-text);
  position: sticky;
  top: var(--tq-space-topbar);
  z-index: 60;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: 'brand menu';
  align-items: center;
  gap: 0.75rem;
  width: min(100%, 112rem);
  margin-inline: auto;
  border: 0;
  border-radius: 0.55rem;
  background: var(--tq-topbar-bg, var(--tq-bg-elevated));
  /* Keep the brand and keyboard focus outline clear of the header edges. */
  padding: 0.65rem clamp(1rem, 1.5vw, 1.25rem);
  box-shadow:
    0 1.2rem 3rem color-mix(in oklch, var(--tq-color-navy) 14%, transparent),
    inset 0 0 0 1px var(--tq-nav-border);
  backdrop-filter: none;
  color: var(--tq-topbar-text);
  isolation: isolate;
  transition:
    background 460ms var(--tq-ease-soft),
    color 320ms var(--tq-ease-out),
    box-shadow 460ms var(--tq-ease-soft);
}

.public-topbar::before {
  position: absolute;
  z-index: -1;
  right: 0;
  bottom: 0;
  left: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--tq-nav-border) 18%,
    var(--tq-nav-active-text) 52%,
    var(--tq-nav-icon) 78%,
    transparent
  );
  content: '';
  opacity: 0.72;
  transform: scaleX(0.96);
}

.public-topbar__brand {
  position: relative;
  display: inline-flex;
  grid-area: brand;
  width: clamp(10rem, 15vw, 12rem);
  height: 3.65rem;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  isolation: isolate;
}

.public-topbar__logo-stage {
  width: 100%;
  height: 3.55rem;

}

.public-topbar__menu-button {
  position: relative;
  display: grid;
  grid-area: menu;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  box-shadow: none;
  color: var(--tq-topbar-text);
  cursor: pointer;
  transition:
    background 220ms var(--tq-ease-out),
    color 220ms var(--tq-ease-out),
    transform 220ms var(--tq-ease-out);
}

.public-topbar__menu-button::before {
  position: absolute;
  inset: 0.35rem;
  border-radius: inherit;
  background: var(--tq-nav-hover-bg);
  content: '';
  opacity: 0;
  transform: scale(0.65);
  transition:
    opacity 220ms var(--tq-ease-out),
    transform 300ms var(--tq-ease-soft);
}

.public-topbar__menu-button[aria-expanded='true']::before,
.public-topbar__menu-button:hover::before {
  opacity: 1;
  transform: scale(1);
}

.public-topbar__menu-icon {
  position: relative;
  width: 1.35rem;
  height: 1.35rem;
  animation: public-menu-icon-in 260ms var(--tq-ease-soft) both;
}

.public-topbar__panel {
  --public-navigation-text: var(--tq-nav-text);
  --public-navigation-icon: var(--tq-nav-icon);
  --public-navigation-muted: var(--tq-nav-text-muted);
  position: absolute;
  top: calc(100% + 0.42rem);
  right: 0.2rem;
  left: 0.2rem;
  display: grid;
  max-height: min(32rem, calc(100svh - 6.5rem));
  overflow: hidden;
  border: 0;
  border-radius: 0.8rem;
  background-color: var(--tq-nav-bg, var(--tq-bg-elevated));
  background-image: var(--tq-gradient-navigation);
  padding: 0.75rem;
  box-shadow:
    0 2rem 5rem color-mix(in oklch, var(--tq-color-navy) 26%, transparent),
    inset 0 0 0 1px var(--tq-nav-border);
  backdrop-filter: none;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-0.9rem);
  transform-origin: top center;
  transition:
    opacity 220ms var(--tq-ease-out),
    transform 360ms var(--tq-ease-soft),
    visibility 0s linear 360ms;
}

.public-topbar__panel--open {
  overflow-y: auto;
  overscroll-behavior: contain;
  visibility: visible;
  transition-delay: 0s;
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.public-topbar__panel > * {
  min-height: 0;
}

.public-topbar__nav {
  display: grid;
  gap: 0;
  border: 0;
  padding: 0;
}

.public-topbar__nav a,
.public-topbar__mobile-access a,
.public-topbar__account {
  display: inline-flex;
  align-items: center;
  gap: 0.72rem;
  border-radius: 0.25rem;
  color: var(--public-navigation-text);
  font-family: inherit;
  font-size: 1rem;
  font-weight: var(--tq-font-weight-navigation);
  letter-spacing: -0.012em;
  transition:
    color 220ms var(--tq-ease-out),
    background 220ms var(--tq-ease-out),
    opacity 260ms var(--tq-ease-out),
    transform 220ms var(--tq-ease-out);
}

.public-topbar__nav a {
  position: relative;
  min-height: 3.25rem;
  padding: 0.72rem 0.68rem 0.72rem 1rem;
  opacity: 0;
  transform: translateY(-0.5rem);
}

.public-topbar__panel--open .public-topbar__nav a {
  opacity: 1;
  transform: translateY(0);
}

.public-topbar__panel--open .public-topbar__nav a:nth-child(1) {
  transition-delay: 40ms;
}

.public-topbar__panel--open .public-topbar__nav a:nth-child(2) {
  transition-delay: 75ms;
}

.public-topbar__nav a::before {
  position: absolute;
  top: 50%;
  left: 0;
  width: 3px;
  height: 1.2rem;
  border-radius: 99px;
  background: var(--tq-nav-active-text);
  content: '';
  opacity: 0;
  transform: translateY(-50%) scaleY(0.2);
  transition:
    opacity 220ms var(--tq-ease-out),
    transform 300ms var(--tq-ease-soft);
}

.public-topbar__nav a.public-topbar__nav-link--active,
.public-topbar__nav a:hover {
  background: var(--tq-nav-hover-bg);
  color: var(--tq-nav-text);
}

.public-topbar__nav a.public-topbar__nav-link--active {
  background: var(--tq-nav-active-bg);
  color: var(--tq-nav-active-text);
}

.public-topbar__nav a.public-topbar__nav-link--active::before {
  opacity: 1;
  transform: translateY(-50%) scaleY(1);
}

.public-topbar__nav :deep(.iconify) {
  width: 1.12rem;
  height: 1.12rem;
  color: var(--public-navigation-icon);
  flex: 0 0 auto;
  transition:
    color 220ms var(--tq-ease-out),
    filter 220ms var(--tq-ease-out),
    transform 320ms var(--tq-ease-soft);
}

.public-topbar__nav a:hover :deep(.iconify) {
  color: var(--tq-nav-icon);
}

.public-topbar__nav a.public-topbar__nav-link--active :deep(.iconify) {
  color: var(--tq-nav-active-text);
  filter: none;
}

.public-topbar__mobile-access {
  display: flex;
  flex-wrap: wrap;
  min-height: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: 0;
  margin-top: 0.4rem;
  padding: 0.72rem 0.25rem 0.1rem;
  box-shadow: inset 0 1px var(--tq-nav-border);
}

.public-topbar__mobile-access a {
  min-height: 2.75rem;
  padding: 0.55rem 0.65rem;
}

.public-topbar__mobile-controls {
  display: flex;
  min-height: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  padding: 0.45rem 0.1rem 0.1rem;
}

.public-topbar__controls {
  display: none;
  grid-area: controls;
  align-items: center;
  justify-content: flex-end;
  gap: 0.2rem;
  min-width: max-content;
}

/* The account panel anchors to the topbar edge, like the public navigation panel. */
.public-topbar--session {
  --account-menu-inset: 0.2rem;
}

/* An open topbar panel must cover the floating cart trigger (z-index 65). */
.public-topbar:has(.public-topbar__panel--open, .account-menu__panel--open) {
  z-index: 70;
}

.public-topbar__session {
  display: flex;
}

.public-topbar__rights {
  margin-inline-end: 0.35rem;
  white-space: nowrap;
}

.public-topbar__account-general {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.1rem;
  border-top: 1px solid var(--tq-nav-border);
  padding-top: 0.4rem;
}

/* Signed-in phones replace the hamburger with the avatar: its panel carries Home, Events, locale and theme. */
@media (max-width: 767.98px) {
  .public-topbar--session {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas: 'brand controls';
  }

  .public-topbar--session .public-topbar__controls {
    display: flex;
  }

  .public-topbar--session .public-topbar__controls > :not(.public-topbar__session) {
    display: none;
  }
}

@media (min-width: 768px) {
  .public-topbar__account-general {
    display: none;
  }
}

.public-topbar__account {
  position: relative;
  min-height: 2.7rem;
  padding: 0.55rem 0.7rem;
}

.public-topbar__account :deep(.iconify) {
  width: 1.1rem;
  height: 1.1rem;
  color: var(--public-navigation-icon);
}

.public-topbar__controls :deep(.locale-switcher),
.public-topbar__mobile-controls :deep(.locale-switcher) {
  height: 2.7rem;
  border: 0;
  border-radius: 0.35rem;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.public-topbar__controls :deep(.locale-switcher__option),
.public-topbar__mobile-controls :deep(.locale-switcher__option) {
  color: var(--public-navigation-text);
  min-width: 2.45rem;
  height: 2.45rem;
  border-radius: 0.3rem;
  font-size: 0.79rem;
}

.public-topbar__controls :deep(.locale-switcher__option--active),
.public-topbar__mobile-controls :deep(.locale-switcher__option--active) {
  background: var(--tq-nav-active-bg);
  color: var(--tq-nav-active-text);
  box-shadow: inset 0 -2px var(--public-topbar-line);
}

.public-topbar__controls :deep(.theme-toggle),
.public-topbar__mobile-controls :deep(.theme-toggle) {
  color: var(--public-navigation-text);
  width: 2.7rem;
  height: 2.7rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  box-shadow: none;
}

.public-topbar__brand:focus-visible,
.public-topbar__nav a:focus-visible,
.public-topbar__mobile-access a:focus-visible,
.public-topbar__account:focus-visible,
.public-topbar__menu-button:focus-visible {
  outline: 3px solid var(--tq-focus);
  outline-offset: 3px;
}

@media (min-width: 768px) {
  .public-topbar {
    grid-template-columns: clamp(10rem, 14vw, 12rem) minmax(0, 1fr) auto;
    grid-template-areas: 'brand panel controls';
    gap: clamp(0.35rem, 0.75vw, 0.8rem);
    border-radius: 0.55rem;
  }

  .public-topbar__brand {
    width: 100%;
  }

  .public-topbar__menu-button {
    display: none;
  }

  .public-topbar__panel {
    --public-navigation-text: var(--tq-topbar-text);
    --public-navigation-icon: var(--tq-topbar-text);
    --public-navigation-muted: var(--tq-topbar-text);
    position: static;
    grid-area: panel;
    display: block;
    min-width: 0;
    max-height: none;
    overflow: visible;
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
    box-shadow: none;
    backdrop-filter: none;
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
    transform: none;
  }

  .public-topbar__nav {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    gap: 0.35rem;
    width: 100%;
    min-width: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
    box-shadow: none;
  }

  .public-topbar__nav a {
    min-width: 0;
    min-height: 3rem;
    justify-content: center;
    border: 0;
    border-radius: 0.3rem;
    padding: 0.58rem 0.8rem;
    font-size: clamp(0.82rem, 0.75vw, 0.92rem);
    opacity: 1;
    transform: none;
    white-space: nowrap;
  }

  .public-topbar__nav a::before {
    top: auto;
    right: 30%;
    bottom: 0.18rem;
    left: 30%;
    width: auto;
    height: 2px;
    background: var(--tq-nav-active-text);
    transform: scaleX(0);
    transform-origin: center;
  }

  .public-topbar__nav a.public-topbar__nav-link--active::before,
  .public-topbar__nav a:hover::before {
    opacity: 1;
    transform: scaleX(1);
  }

  .public-topbar__nav a.public-topbar__nav-link--active {
    background: var(--tq-nav-active-bg);
    color: var(--tq-nav-active-text);
  }

  .public-topbar__nav :deep(.iconify) {
    display: block;
    width: 1rem;
    height: 1rem;
  }

  .public-topbar__mobile-access {
    display: none;
  }

  .public-topbar__mobile-controls {
    display: none;
  }

  .public-topbar__controls {
    display: flex;
  }

  .public-topbar__account {
    width: 2.7rem;
    justify-content: center;
    padding-inline: 0;
  }

  .public-topbar__account .public-topbar__access-label {
    display: none;
  }
}

@media (min-width: 1440px) {
  .public-topbar {
    grid-template-columns: minmax(8.8rem, 10.5rem) minmax(0, 1fr) auto;
  }

  .public-topbar__account {
    width: auto;
    justify-content: flex-start;
    padding-inline: 0.7rem;
  }

  .public-topbar__account .public-topbar__access-label {
    display: inline;
  }
}

@media (min-width: 1600px) {
  .public-topbar {
    grid-template-columns: 14rem minmax(0, 1fr) auto;
    gap: clamp(0.7rem, 1.25vw, 1.45rem);
  }

  .public-topbar__brand {
    width: 100%;
  }

  .public-topbar__nav a {
    padding-inline: clamp(0.56rem, 0.72vw, 0.84rem);
    font-size: clamp(0.9rem, 0.82vw, 1rem);
  }
}

@media (hover: hover) and (pointer: fine) {
  .public-topbar__nav a:hover,
  .public-topbar__account:hover,
  .public-topbar__menu-button:hover {
    transform: translateY(-2px);
  }

  .public-topbar__nav a:hover :deep(.iconify) {
    color: var(--public-navigation-icon);
    transform: translateY(-1px) rotate(-4deg);
  }

  .public-topbar__nav a.public-topbar__nav-link--active:hover :deep(.iconify) {
    color: var(--tq-nav-active-text);
  }

  .public-topbar__account:hover {
    background: var(--tq-nav-hover-bg);
    color: var(--tq-nav-text);
  }
}

@keyframes public-menu-icon-in {
  from {
    opacity: 0;
    transform: rotate(-18deg) scale(0.72);
  }

  to {
    opacity: 1;
    transform: rotate(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .public-topbar,
  .public-topbar__menu-button,
  .public-topbar__menu-button::before,
  .public-topbar__panel,
  .public-topbar__nav a,
  .public-topbar__nav a::before,
  .public-topbar__nav :deep(.iconify),
  .public-topbar__account {
    transition: none;
  }

  .public-topbar__menu-icon {
    animation: none;
  }
}

@media (forced-colors: active) {
  .public-topbar {
    border-bottom: 1px solid CanvasText;
  }

  .public-topbar__nav a.public-topbar__nav-link--active {
    outline: 2px solid LinkText;
    outline-offset: -2px;
  }
}
</style>
